import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Link from '@mui/material/Link'
import type { FormField } from '../../model/form'

/**
 * What a visitor answered, rendered for a reader.
 *
 * **Two renderings, and the boundary between them is the verdict.** A
 * legitimate enquiry with a website in it should be one click from that
 * website; a submission a defence stopped is rendered inert, and that
 * rule is the one that stops the platform becoming a phishing delivery
 * mechanism.
 *
 * Escaping is not the concern — React escapes by default, and the email
 * renderer escapes both of its halves. Escaping does nothing about a
 * *plausible link the reader clicks*, in a session with more privilege
 * than their webmail, and most table components linkify URLs and
 * addresses without anybody deciding they should.
 *
 * So `inert` is a prop on one component rather than a policy spread
 * across a screen: the rule has a file and a test rather than a
 * reviewer's memory.
 */
export function SubmittedValues({
  values,
  fields,
  inert,
}: {
  values: Record<string, unknown>
  fields: FormField[] | undefined
  inert: boolean
}) {
  // Emptiness is read off the *values*, not off the rows: a form with
  // fields and no answers would otherwise render a table of blanks, which
  // is what a discarded submission is and reads as a bug.
  const rows = Object.keys(values).length === 0 ? [] : rowsOf(values, fields)
  if (rows.length === 0) {
    return <Typography color="text.secondary">This submission holds no answers. They were discarded.</Typography>
  }
  return (
    <Stack spacing={1.5} component="dl" sx={{ m: 0 }}>
      {rows.map(([label, answer]) => (
        <Box key={label}>
          <Typography component="dt" variant="overline" color="text.secondary">
            {label}
          </Typography>
          <Typography component="dd" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            <Answer text={answer} inert={inert} />
          </Typography>
        </Box>
      ))}
    </Stack>
  )
}

/**
 * One answer.
 *
 * Inert means three things, and all three are asserted by a test:
 *
 *   - every value is **plain text** — a URL is a string, an address is a
 *     string, and neither is ever an anchor;
 *   - **no remote resource** is loaded — no images, no favicons, no
 *     previews;
 *   - **nothing is linkified**, and this is the one place in the admin
 *     where that is stated rather than inherited.
 */
function Answer({ text, inert }: { text: string; inert: boolean }) {
  if (inert || !text) return <>{text}</>
  const target = linkOf(text)
  if (!target) return <>{text}</>
  return (
    <Link href={target} target="_blank" rel="noopener noreferrer nofollow">
      {text}
    </Link>
  )
}

/**
 * The answer as a link, and undefined for anything that is not plainly
 * one.
 *
 * Deliberately narrow: a whole value that is one `http`/`https` URL or
 * one email address. Nothing is scanned for links inside prose, because a
 * message is prose and picking links out of it is how a rendering
 * acquires the behaviour the inert half exists to refuse.
 */
function linkOf(text: string): string | undefined {
  const value = text.trim()
  if (value !== text || value.includes(' ')) return undefined
  if (/^https?:\/\/\S+$/i.test(value)) return value
  if (/^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(value)) return `mailto:${value}`
  return undefined
}

/**
 * The answers in the form's own order, labelled by what the form asked.
 *
 * **The form's order, never the object's.** The values arrive as an
 * object with no order at all, so walking them would list the same form
 * differently on consecutive readings — the same argument the email
 * renderer makes, and the same answer. A field the visitor left blank
 * still appears, which is what makes two enquiries comparable at a
 * glance.
 *
 * A value with no field beside it is still shown, at the end: the form is
 * read at the revision the visitor answered, so this should be empty, and
 * a submission that somehow carries an extra answer is better shown than
 * silently dropped.
 */
function rowsOf(values: Record<string, unknown>, fields: FormField[] | undefined): [string, string][] {
  const rows: [string, string][] = []
  const seen = new Set<string>()
  for (const field of fields ?? []) {
    seen.add(field.name)
    rows.push([field.label || field.name, answerText(values[field.name])])
  }
  for (const [name, value] of Object.entries(values)) {
    if (seen.has(name)) continue
    rows.push([name, answerText(value)])
  }
  return rows
}

function answerText(value: unknown): string {
  if (value === undefined || value === null) return ''
  if (Array.isArray(value)) return value.map(answerText).join(', ')
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}
